// src/pages/AdminTarifario.js
// =========================================================================
// TARIFARIO Y BONOS (solo admin)
//
// Las especialidades de la clínica no se eligen aquí: son las de su personal
// sanitario (la de cada profesional la fija el soporte de Klyndu desde el
// servidor). El administrador ajusta los servicios de cada una (duración,
// precio e IVA) y los bonos de sesiones.
// =========================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useClinica } from '../context/ClinicaContext';
import IconoEspecialidad from '../components/clinica/IconoEspecialidad';
import TarjetaRecordatorios from '../components/clinica/TarjetaRecordatorios';

// Los códigos guardados no se cambian (los usan citas, bonos y facturas). Los
// nuevos llevan fijas las siglas de su especialidad (MG-, FT-…) o BONO-, y solo
// se escribe lo que va detrás.
const PREFIJO_BONO = 'BONO';
const limpiarSufijo = (texto) => texto.toUpperCase().replace(/[^A-Z0-9_-]/g, '');

function CeldaCodigo({ codigo, prefijo, guardado, etiqueta, onChange }) {
    if (guardado) {
        return (
            <span title="El código no se puede cambiar: lo usan las citas, los bonos y las facturas."
                  className="block px-3 py-2 text-sm font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-lg truncate">{codigo}</span>
        );
    }
    const sufijo = codigo.startsWith(`${prefijo}-`) ? codigo.slice(prefijo.length + 1) : codigo;
    return (
        <div className="flex items-stretch">
            <span className="px-2 py-2 text-sm font-black text-slate-500 bg-slate-100 border border-r-0 border-slate-200 rounded-l-lg">{prefijo}-</span>
            <input aria-label={etiqueta} value={sufijo} onChange={e => onChange(`${prefijo}-${limpiarSufijo(e.target.value)}`)}
                   className="w-full min-w-0 px-2 py-2 bg-slate-50 border border-slate-200 rounded-r-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15" />
        </div>
    );
}

const entrada = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15';

export default function AdminTarifario() {
    const { showToast } = useToast();
    const toastRef = useRef(showToast);
    toastRef.current = showToast;
    const { actualizarLocal } = useClinica();

    const [especialidades, setEspecialidades] = useState([]); // las del personal sanitario
    const [definiciones, setDefiniciones] = useState({});
    const [servicios, setServicios] = useState([]);
    const [bonos, setBonos] = useState([]);
    const [codigosGuardados, setCodigosGuardados] = useState(new Set());
    const [recordatoriosActivos, setRecordatoriosActivos] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState('');

    const aplicar = useCallback((config) => {
        setEspecialidades(config.especialidades || []);
        setDefiniciones(config.definiciones || {});
        setServicios(config.servicios || []);
        setBonos(config.bonos || []);
        setRecordatoriosActivos(Boolean(config.recordatorios?.activos));
        setCodigosGuardados(new Set([...(config.servicios || []), ...(config.bonos || [])].map(x => x.codigo.toUpperCase())));
        actualizarLocal(config);
    }, [actualizarLocal]);

    useEffect(() => {
        (async () => {
            try {
                aplicar(await api.get('/clinica/configuracion'));
            } catch (err) {
                toastRef.current(err.message || 'No se pudo cargar el tarifario.', 'error');
            } finally {
                setCargando(false);
            }
        })();
    }, [aplicar]);

    const porId = definiciones;

    const cambiarServicio = (indice, campo, valor) =>
        setServicios(lista => lista.map((s, i) => (i === indice ? { ...s, [campo]: valor } : s)));

    const anadirServicio = (especialidad) => {
        const prefijo = porId[especialidad]?.prefijo || especialidad.slice(0, 2).toUpperCase();
        const usados = new Set(servicios.map(s => s.codigo.toUpperCase()));
        let n = servicios.length + 1;
        while (usados.has(`${prefijo}-${n}`)) n++;
        setServicios(lista => [...lista, {
            codigo: `${prefijo}-${n}`, nombre: '', especialidad,
            duracion: porId[especialidad]?.servicios?.[0]?.duracion || 30, precio: 0,
            iva: porId[especialidad]?.servicios?.[0]?.iva ?? 0, activo: true
        }]);
    };

    // 🎟️ BONOS (packs de sesiones de un servicio)
    const cambiarBono = (indice, campo, valor) =>
        setBonos(lista => lista.map((b, i) => (i === indice ? { ...b, [campo]: valor } : b)));

    const anadirBono = () => {
        const s = servicios.find(x => x.activo && x.precio > 0) || servicios[0];
        if (!s) return;
        const usados = new Set(bonos.map(b => b.codigo.toUpperCase()));
        let n = bonos.length + 1;
        while (usados.has(`${PREFIJO_BONO}-${n}`)) n++;
        setBonos(lista => [...lista, {
            codigo: `${PREFIJO_BONO}-${n}`, nombre: `Bono 5 × ${s.nombre}`, especialidad: s.especialidad, servicio: s.codigo,
            sesiones: 5, precio: Math.round(s.precio * 5 * 0.9), validezDias: 180, activo: true
        }]);
    };

    // Bonos sugeridos de cada especialidad que aún no están
    const sugeridos = especialidades.flatMap(id => (porId[id]?.bonos || []).map(b => ({ ...b, especialidad: id, activo: true })))
        .filter(b => !bonos.some(x => x.codigo.toUpperCase() === b.codigo.toUpperCase()))
        .filter(b => servicios.some(s => s.codigo === b.servicio));

    const guardarBonos = async () => {
        setGuardando('bonos');
        try {
            const payload = bonos.map(b => ({ ...b, sesiones: Number(b.sesiones), precio: Number(b.precio), validezDias: Number(b.validezDias) }));
            aplicar(await api.put('/clinica/bonos', { bonos: payload }));
            showToast('Bonos guardados.', 'success');
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setGuardando('');
        }
    };

    const guardarServicios = async () => {
        setGuardando('servicios');
        try {
            const payload = servicios.map(s => ({ ...s, duracion: Number(s.duracion), precio: Number(s.precio), iva: Number(s.iva) }));
            aplicar(await api.put('/clinica/servicios', { servicios: payload }));
            showToast('Tarifario guardado.', 'success');
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setGuardando('');
        }
    };

    if (cargando) {
        return (
            <div className="p-6 md:p-8 max-w-5xl mx-auto">
                <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse mb-6"></div>
                <div className="h-96 bg-white border border-slate-200 rounded-[2rem] animate-pulse"></div>
            </div>
        );
    }

    const especialidadesTarifario = especialidades;

    return (
        <div className="p-6 md:p-8 space-y-8 max-w-5xl mx-auto animate-fade-in">
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Tarifario</h1>
                <p className="text-sm font-semibold text-slate-500 mt-1">
                    Servicios y bonos de las especialidades de tu personal. Si necesitas una especialidad nueva, el soporte de Klyndu da de alta al profesional con ella.
                </p>
            </div>

            {/* 1. SERVICIOS */}
            <section className="bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-6">
                <div>
                    <h2 className="text-lg font-black text-slate-900">1. Servicios</h2>
                    <p className="text-xs font-semibold text-slate-500 mt-1">
                        Servicios que se ofrecen al dar una cita. Los sanitarios suelen ir exentos de IVA; la estética sin fin terapéutico, al 21 %.
                    </p>
                </div>

                <>
                        {especialidadesTarifario.map(idEsp => {
                            const esp = porId[idEsp];
                            const filas = servicios.map((s, i) => [s, i]).filter(([s]) => s.especialidad === idEsp);
                            return (
                                <div key={idEsp} className="space-y-3">
                                    <h3 className="text-sm font-black text-slate-700 flex items-center gap-2">
                                        <IconoEspecialidad icono={esp?.icono} fontSize="small" style={{ color: esp?.color }} /> {esp?.nombre || idEsp}
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[640px] text-sm">
                                            <thead>
                                                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-left">
                                                    <th className="pb-2 pr-2 w-40">Código</th>
                                                    <th className="pb-2 pr-2">Servicio</th>
                                                    <th className="pb-2 pr-2 w-24">Minutos</th>
                                                    <th className="pb-2 pr-2 w-28">Precio €</th>
                                                    <th className="pb-2 pr-2 w-24">IVA</th>
                                                    <th className="pb-2 pr-2 w-16 text-center">Activo</th>
                                                    <th className="pb-2 w-10"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filas.map(([s, i]) => (
                                                    <tr key={i} className={s.activo ? '' : 'opacity-50'}>
                                                        <td className="py-1 pr-2"><CeldaCodigo codigo={s.codigo} prefijo={esp?.prefijo || ''} guardado={codigosGuardados.has(s.codigo.toUpperCase())} etiqueta="Código" onChange={v => cambiarServicio(i, 'codigo', v)} /></td>
                                                        <td className="py-1 pr-2"><input aria-label="Servicio" className={entrada} value={s.nombre} onChange={e => cambiarServicio(i, 'nombre', e.target.value)} placeholder="Nombre del servicio" /></td>
                                                        <td className="py-1 pr-2"><input aria-label="Minutos" type="number" min="5" max="240" step="5" className={entrada} value={s.duracion} onChange={e => cambiarServicio(i, 'duracion', e.target.value)} /></td>
                                                        <td className="py-1 pr-2"><input aria-label="Precio" type="number" min="0" step="0.01" className={entrada} value={s.precio} onChange={e => cambiarServicio(i, 'precio', e.target.value)} /></td>
                                                        <td className="py-1 pr-2">
                                                            <select aria-label="IVA" className={entrada} value={s.iva} onChange={e => cambiarServicio(i, 'iva', Number(e.target.value))}>
                                                                {[0, 4, 10, 21].map(v => <option key={v} value={v}>{v === 0 ? 'Exento' : `${v} %`}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="py-1 pr-2 text-center"><input aria-label="Activo" type="checkbox" className="w-4 h-4" checked={s.activo} onChange={e => cambiarServicio(i, 'activo', e.target.checked)} /></td>
                                                        <td className="py-1">
                                                            <button type="button" aria-label={`Quitar ${s.nombre || s.codigo}`} onClick={() => setServicios(l => l.filter((_, j) => j !== i))}
                                                                    className="w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 font-black">×</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button type="button" onClick={() => anadirServicio(idEsp)} className="text-xs font-black text-clinica-primary hover:underline">
                                        + Añadir servicio
                                    </button>
                                </div>
                            );
                        })}
                        <div className="flex justify-end">
                            <button type="button" onClick={guardarServicios} disabled={guardando === 'servicios'}
                                    className="px-6 py-3 rounded-xl text-sm font-black text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 shadow-md shadow-emerald-500/20">
                                {guardando === 'servicios' ? 'Guardando…' : 'Guardar tarifario'}
                            </button>
                        </div>
                    </>
            </section>

            {/* 2. BONOS */}
            <section className="bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-6">
                <div>
                    <h2 className="text-lg font-black text-slate-900">2. Bonos de sesiones</h2>
                    <p className="text-xs font-semibold text-slate-500 mt-1">
                        Packs de varias sesiones de un servicio a precio cerrado. Se venden desde la ficha del paciente y cada cita descuenta una sesión.
                    </p>
                </div>

                <>
                        {bonos.length === 0 && <p className="text-sm font-semibold text-slate-400">No hay bonos configurados.</p>}
                        {bonos.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[860px] text-sm">
                                    <thead>
                                        <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-left">
                                            <th className="pb-2 pr-2 w-44">Código</th>
                                            <th className="pb-2 pr-2">Nombre</th>
                                            <th className="pb-2 pr-2 w-48">Servicio</th>
                                            <th className="pb-2 pr-2 w-20">Sesiones</th>
                                            <th className="pb-2 pr-2 w-24">Precio €</th>
                                            <th className="pb-2 pr-2 w-24">Validez (días)</th>
                                            <th className="pb-2 pr-2 w-16 text-center">Activo</th>
                                            <th className="pb-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bonos.map((b, i) => (
                                            <tr key={i} className={b.activo ? '' : 'opacity-50'}>
                                                <td className="py-1 pr-2"><CeldaCodigo codigo={b.codigo} prefijo={PREFIJO_BONO} guardado={codigosGuardados.has(b.codigo.toUpperCase())} etiqueta="Código del bono" onChange={v => cambiarBono(i, 'codigo', v)} /></td>
                                                <td className="py-1 pr-2"><input aria-label="Nombre del bono" className={entrada} value={b.nombre} onChange={e => cambiarBono(i, 'nombre', e.target.value)} /></td>
                                                <td className="py-1 pr-2">
                                                    <select aria-label="Servicio del bono" className={entrada} value={b.servicio}
                                                            onChange={e => { const s = servicios.find(x => x.codigo === e.target.value); setBonos(l => l.map((x, j) => (j === i ? { ...x, servicio: e.target.value, especialidad: s?.especialidad || x.especialidad } : x))); }}>
                                                        {servicios.map(s => <option key={s.codigo} value={s.codigo}>{s.nombre}</option>)}
                                                    </select>
                                                </td>
                                                <td className="py-1 pr-2"><input aria-label="Sesiones" type="number" min="2" max="100" className={entrada} value={b.sesiones} onChange={e => cambiarBono(i, 'sesiones', e.target.value)} /></td>
                                                <td className="py-1 pr-2"><input aria-label="Precio del bono" type="number" min="0" step="0.01" className={entrada} value={b.precio} onChange={e => cambiarBono(i, 'precio', e.target.value)} /></td>
                                                <td className="py-1 pr-2"><input aria-label="Validez en días (0 = no caduca)" type="number" min="0" max="1095" className={entrada} value={b.validezDias} onChange={e => cambiarBono(i, 'validezDias', e.target.value)} /></td>
                                                <td className="py-1 pr-2 text-center"><input aria-label="Bono activo" type="checkbox" className="w-4 h-4" checked={b.activo} onChange={e => cambiarBono(i, 'activo', e.target.checked)} /></td>
                                                <td className="py-1">
                                                    <button type="button" aria-label={`Quitar ${b.nombre || b.codigo}`} onClick={() => setBonos(l => l.filter((_, j) => j !== i))}
                                                            className="w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 font-black">×</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <p className="text-[11px] font-semibold text-slate-400">Validez 0 = el bono no caduca. Los bonos ya vendidos conservan sus condiciones aunque cambies estas.</p>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-4">
                                <button type="button" onClick={anadirBono} className="text-xs font-black text-clinica-primary hover:underline">+ Añadir bono</button>
                                {sugeridos.length > 0 && (
                                    <button type="button" onClick={() => setBonos(l => [...l, ...sugeridos])} className="text-xs font-black text-emerald-700 hover:underline">
                                        + Añadir {sugeridos.length} bono{sugeridos.length > 1 ? 's' : ''} sugerido{sugeridos.length > 1 ? 's' : ''}
                                    </button>
                                )}
                            </div>
                            <button type="button" onClick={guardarBonos} disabled={guardando === 'bonos'}
                                    className="px-6 py-3 rounded-xl text-sm font-black text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 shadow-md shadow-emerald-500/20">
                                {guardando === 'bonos' ? 'Guardando…' : 'Guardar bonos'}
                            </button>
                        </div>
                    </>
            </section>

            {/* 3. RECORDATORIOS */}
            <TarjetaRecordatorios key={String(recordatoriosActivos)} numero={3} activosIniciales={recordatoriosActivos} />
        </div>
    );
}
